/*
 * @Description: ComparisonPart组件 - 展示镜片对比信息
 * 从Vue组件转换而来，支持响应式设计，移动端使用轮播，PC端使用网格布局
 */
'use client';

import Image from '@/components/Image';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useMediaQuery } from '@mui/material';
import { CheckOutlined } from '@zeelool/icon';
import React from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// 定义数据项的类型
interface ComparisonItem {
  image: string;
  title: string;
  title_m?: string;
  description: string[];
  description_m?: string[];
}

// 定义组件props的类型
interface ComparisonData {
  title: string;
  title_m?: string;
  data_array: ComparisonItem[];
}

interface ComparisonPartProps {
  comparisonData: ComparisonData;
}

/**
 * ComparisonPart组件 - 展示镜片对比信息
 * @param comparisonData - 包含对比数据的对象
 */
const ComparisonPart: React.FC<ComparisonPartProps> = ({ comparisonData }) => {
  // 获取设备类型
  const isMobileScreen = useMediaQuery('(max-width: 768px)');

  // 如果没有数据，不渲染组件
  if (!comparisonData?.data_array?.length) {
    return null;
  }

  // 获取当前设备对应的标题
  const currentTitle = isMobileScreen
    ? comparisonData.title_m || comparisonData.title
    : comparisonData.title;

  // 移动端轮播组件
  const renderMobileSwiper = () => (
    <div className="swiper-nav-container relative">
      <Swiper
        modules={[Navigation]}
        slidesPerView="auto"
        spaceBetween={8}
        slidesOffsetBefore={14}
        slidesOffsetAfter={14}
        observer
        observeParents
        observeSlideChildren
        navigation={{
          nextEl: '.comparison-next',
          prevEl: '.comparison-prev',
        }}
        className="w-full h-[400px]"
      >
        {comparisonData.data_array.map((item, index) => {
          const currentItemTitle = item.title_m || item.title;
          const currentDescription = item.description_m || item.description;

          return (
            <SwiperSlide key={index} className="!w-[320px] !h-full">
              <div className="comparison-item h-full rounded-[6px] border border-[#d4d4d4] overflow-hidden flex flex-col">
                <div className="comparison-item-box flex flex-col h-full">
                  <div className="comparison-item-box-top flex-shrink-0">
                    <Image
                      src={item.image}
                      alt={currentItemTitle}
                      width={320}
                      height={180}
                      className="w-full h-[180px] object-cover block"
                    />
                  </div>
                  <div className="comparison-item-box-bottom p-[16px_12px] w-full box-border flex-1 flex flex-col">
                    <h4 className="text-[16px] font-bb mb-[16px]">{currentItemTitle}</h4>
                    <ul className="comparison-desc-list flex-1">
                      {currentDescription.map((desc, idx) => (
                        <li key={idx} className="text-[13px] flex items-center mb-[12px]">
                          <CheckOutlined className="mr-[10px] text-[13px] md:text-[12px] shrink-0" />
                          {desc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* 导航按钮 */}
      <SwiperNavigationButtons
        navigationPrefix="comparison"
        prevButtonPosition="left-2"
        nextButtonPosition="right-2"
      />
    </div>
  );

  // PC端网格布局组件
  const renderDesktopGrid = () => (
    <ul className="flex justify-between items-stretch flex-nowrap gap-[24px]">
      {comparisonData.data_array.map((item, index) => (
        <li
          key={index}
          className="w-[708px] xl:w-[708px] lg:w-[560px] md:w-[480px] flex flex-col items-center rounded-[12px] overflow-hidden border border-[#d4d4d4] box-border"
        >
          <div className="comparison-item-box flex flex-col items-center w-full">
            <div className="comparison-item-box-top rounded-t-[12px] overflow-hidden w-full">
              <Image
                src={item.image}
                alt={item.title}
                width={708}
                height={398}
                className="w-full h-[398px] xl:h-[398px] lg:h-[320px] md:h-[280px] object-cover block"
              />
            </div>
            <div className="comparison-item-box-bottom p-[23px_32px] xl:p-[23px_32px] lg:p-[20px_28px] md:p-[18px_24px] w-full box-border">
              <h4 className="text-[20px] xl:text-[20px] lg:text-[18px] md:text-[16px] font-bb mb-[21px] xl:mb-[21px] lg:mb-[18px] md:mb-[16px]">
                {item.title}
              </h4>
              <ul className="comparison-desc-list grid grid-cols-2 gap-[8px_14px] ">
                {item.description.map((desc, idx) => (
                  <li key={idx} className="text-[0.78vw]  flex items-center ">
                    {/* <i className="mr-[10px] xl:mr-[10px] lg:mr-[8px] md:mr-[6px] text-[14px] xl:text-[14px] lg:text-[12px] md:text-[11px]">
                      ✓
                    </i> */}
                    <CheckOutlined className="mr-[4px] text-[0.83vw] md:text-[15px] shrink-0" />
                    <span className="whitespace-nowrap">{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="lenses-comparison w-full box-border md:px-[160px] lg:px-[180px] md:my-[120px] lg:my-[120px] xl:my-[120px] pb-[40px] md:pb-0">
      <div className="comparison-box mb-[20px] md:mb-[40px] lg:mb-[40px] xl:mb-[40px] px-[14px] md:px-0">
        <h2 className="text-[24px] leading-[29px] md:text-[40px] lg:text-[40px] xl:text-[40px] md:leading-[48px] lg:leading-[48px] xl:leading-[48px] font-bb text-center">
          {currentTitle}
        </h2>
      </div>

      {/* 移动端显示轮播，PC端显示网格 */}
      {isMobileScreen ? renderMobileSwiper() : renderDesktopGrid()}
    </div>
  );
};

export default ComparisonPart;
